// @ts-strict-ignore
import { UpdateAppSettingsSuccessAction } from './../actions/appSettings';
import {
    FEATURE_FLAGS_FOR_RELEASES_UPDATED,
    FeatureFlagsUpdatedAction,
    FetchFeatureFlagsResponse,
    UPDATE_FEATURE_FLAGS,
    UpdateFeatureFlagsSuccessAction,
} from './../actions/featureFlags';
import {
    FETCH_ROLLOUT_MONITORING_SUCCESS,
    FetchRolloutMonitoringSuccessAction,
    ROLLOUT_MONITORING_FOR_RELEASES_UPDATED,
    RolloutMonitoringResponse,
    RolloutMonitoringUpdatedAction,
} from '../actions/rolloutMonitoring';
import { FETCH_RELEASE_STATS_SUCCESS, FetchReleaseStatsHistorySuccessAction } from './../actions/releaseStatsHistory';
import { App, FeatureFlag, Release, ReleaseEntity } from '../types';
import {
    DELETE_RELEASE_ENTITY,
    DELETE_RELEASES,
    DeleteReleaseEntityAction,
    DeleteReleaseEntityPayload,
    DeleteReleasesAction,
    FETCH_RELEASE_SUCCESS,
    FETCH_RELEASES_SUCCESS,
    FetchReleasesSuccessAction,
    FetchReleaseSuccessAction,
    NEW_RELEASE_SUCCESS,
    NewReleaseSuccessAction,
    RELEASE_STATE_UPDATED,
    RELEASES_UPDATED,
    ReleaseStateUpdatedAction,
    ReleasesUpdatedAction,
    UPDATE_RELEASE_ENTITY_SUCCESS,
    UPDATE_RELEASE_SUCCESS,
    UpdateReleaseEntitySuccessAction,
    UpdateReleaseSuccessAction,
} from '../actions/release';
import { LOGOUT_SUCCESS, LogoutSuccessAction } from '../actions/login';
import { difference, forEach, keyBy, reduce, uniq, unset } from 'lodash';
import { combineReducers } from 'redux';
import { CLEAR_STATE, ClearStateAction } from '../actions/state';
import {
    CLEAR_TIMELINE_EVENTS_HISTORY_ACTION,
    ClearTimelineEventsHistoryAction,
    FETCH_TIMELINE_EVENTS_HISTORY_SUCCESS,
    FetchTimelineEventsHistorySuccessAction,
    TIMELINE_EVENTS_HISTORY_UPDATED,
    TimelineEventsHistoryUpdatedAction,
} from '../actions/timelineEventsHistory';
import { FETCH_FEATURE_FLAGS_SUCCESS, FetchFeatureFlagsSuccessAction } from '../actions/featureFlags';
import { FETCH_APP_STATE_SUCCESS, FetchAppStateSuccessAction } from '../actions/app';
import { FETCH_RELEASE_OVERVIEW, FetchReleaseOverviewSuccessAction } from '@/actions/releaseOverview';
import {
    UPDATE_WORKFLOW_RUN_STEP_RUN_GRAPH,
    UpdateWorkflowRunStepRunGraphAction,
} from '@/actions/workflowRunStepRunGraph';

interface ReleasesById {
    [id: string]: ReleaseEntity | Release;
}

type ReleasesActionTypes =
    | UpdateReleaseSuccessAction
    | ReleaseStateUpdatedAction
    | NewReleaseSuccessAction
    | ReleasesUpdatedAction
    | UpdateReleaseEntitySuccessAction
    | DeleteReleaseEntityAction
    | DeleteReleasesAction
    | LogoutSuccessAction
    | FetchReleaseSuccessAction
    | FetchTimelineEventsHistorySuccessAction
    | TimelineEventsHistoryUpdatedAction
    | FetchReleaseStatsHistorySuccessAction
    | FetchRolloutMonitoringSuccessAction
    | FetchFeatureFlagsSuccessAction
    | RolloutMonitoringUpdatedAction
    | FeatureFlagsUpdatedAction
    | ClearStateAction
    | FetchReleaseOverviewSuccessAction
    | UpdateAppSettingsSuccessAction
    | FetchReleasesSuccessAction
    | UpdateWorkflowRunStepRunGraphAction
    | UpdateFeatureFlagsSuccessAction
    | ClearTimelineEventsHistoryAction
    | FetchAppStateSuccessAction;

function updateReleaseEntityWithFullRelease(state: ReleasesById, release: Release): ReleasesById {
    if (!release?.id) {
        return state;
    }

    const releaseId = release.id;

    const timelineEventsHistory = state[releaseId]?.timelineEventsHistory;
    const rolloutMonitoring = state[releaseId]?.rolloutMonitoring;

    return {
        ...state,
        [releaseId]: {
            ...release,
            timelineEventsHistory,
            rolloutMonitoring,
        },
    };
}

function deleteRelease(state, releaseId: string) {
    const updatedState = {
        ...state,
    };

    unset(updatedState, releaseId);

    return updatedState;
}

function deleteReleases(state, releases: DeleteReleaseEntityPayload[]) {
    const updatedState = {
        ...state,
    };

    forEach(releases, (deleteReleaseEntity) => {
        unset(updatedState, deleteReleaseEntity.releaseId);
    });

    return updatedState;
}

function updateReleaseEntity(state: ReleasesById, release: ReleaseEntity, forceFullUpdate = false): ReleasesById {
    const releaseId = release.id;
    const prevRelease = state[releaseId] as Release;

    const timelineEventsHistory = prevRelease?.timelineEventsHistory;
    const releaseStatsHistory = prevRelease?.releaseStatsHistory;
    const releaseOverview = prevRelease?.releaseOverview;
    const rolloutMonitoring = prevRelease?.rolloutMonitoring;
    const featureFlags = prevRelease?.featureFlags;

    if (forceFullUpdate || (prevRelease && prevRelease.stepRunGraph)) {
        // update full release
        return {
            ...state,
            [releaseId]: {
                ...release,
                timelineEventsHistory,
                releaseStatsHistory,
                rolloutMonitoring,
                featureFlags,
                releaseOverview,
            },
        };
    } else {
        // update just the entity
        return {
            ...state,
            [releaseId]: {
                id: release.id,
                releaseId: release.releaseId,
                version: release.version,
                displayableVersion: release.displayableVersion,
                status: release.status,
                timelinePhase: release.timelinePhase,
                releasedAt: release.releasedAt,
                phasedRelease: release.phasedRelease,
                isReleaseTagged: release.isReleaseTagged,
                createdOrigin: release.createdOrigin,
                type: release.type,
                rollbackInfo: release.rollbackInfo,
                rollbackTargetVersionInfo: release.rollbackTargetVersionInfo,
                shouldWarnAboutNewerCommits: release.shouldWarnAboutNewerCommits,
                releaseOverview,
                timelineEventsHistory,
                releaseStatsHistory,
                rolloutMonitoring,
                featureFlags,
            },
        };
    }
}

function updateReleaseEntities(state: ReleasesById, releases: ReleaseEntity[], forceFullUpdate = false) {
    return reduce(
        releases,
        (currentState, currentRelease) => {
            return updateReleaseEntity(currentState, currentRelease, forceFullUpdate);
        },
        state,
    );
}

function allReleases(app: App): ReleaseEntity[] {
    const allReleasesInApp = [...app.releases];
    return allReleasesInApp;
}

function createReleasesById(state: ReleasesById, app: App) {
    const releases = allReleases(app);
    const releasesById = keyBy(releases, (release) => release.id);
    return {
        ...state,
        ...releasesById,
    };
}

function updateReleasesById(state: ReleasesById, releases: ReleaseEntity[]) {
    const releasesById = keyBy(releases, (release) => release.id);

    return {
        ...state,
        ...releasesById,
    };
}

function updateRolloutMonitoringForReleases(state, rolloutMonitoringForReleases: RolloutMonitoringResponse[]) {
    return reduce(
        rolloutMonitoringForReleases,
        (currentState, rolloutMonitoringForRelease) => {
            const currentRelease = currentState[rolloutMonitoringForRelease.releaseId];

            if (currentRelease) {
                return {
                    ...currentState,
                    [currentRelease.id]: {
                        ...currentRelease,
                        rolloutMonitoring: rolloutMonitoringForRelease.rolloutMonitoring,
                    },
                };
            }

            return currentState;
        },
        state,
    );
}

function updateFeatureFlagsForReleases(state, featureFlagsForReleases: FetchFeatureFlagsResponse[]) {
    return reduce(
        featureFlagsForReleases,
        (currentState, featureFlagsForRelease) => {
            const currentRelease = currentState[featureFlagsForRelease.releaseId];
            if (currentRelease) {
                return {
                    ...currentState,
                    [currentRelease.id]: {
                        ...currentRelease,
                        featureFlags: featureFlagsForRelease.featureFlags,
                    },
                };
            }
            return currentState;
        },
        state,
    );
}

function updateIndividualFeatureFlagForReleases(state: ReleasesById, releaseId: string, featureFlags: FeatureFlag[]) {
    const currentRelease = state[releaseId];
    if (currentRelease) {
        const currentFeatureFlags = currentRelease.featureFlags ?? [];
        // Create a map of updated flag IDs to replace old flags
        const updatedFlagIds = new Set(featureFlags.map((flag) => flag.id));
        // Filter out old flags that are being updated, then add the new ones
        const filteredFlags = currentFeatureFlags.filter((flag) => !updatedFlagIds.has(flag.id));
        return {
            ...state,
            [releaseId]: {
                ...currentRelease,
                featureFlags: [...filteredFlags, ...featureFlags],
            },
        };
    }

    return state;
}

function releasesById(state: ReleasesById = {}, action: ReleasesActionTypes) {
    switch (action.type) {
        case FETCH_RELEASE_OVERVIEW: {
            if (action.payload.releaseId) {
                const currentRelease = state[action.payload.releaseId];
                if (currentRelease) {
                    const { statistics, ...rest } = action.payload;

                    return {
                        ...state,
                        [action.payload.releaseId]: {
                            ...currentRelease,
                            releaseStatsHistory: statistics,
                            releaseOverview: rest,
                        },
                    };
                }
            }
            return state;
        }
        case FETCH_TIMELINE_EVENTS_HISTORY_SUCCESS:
        case TIMELINE_EVENTS_HISTORY_UPDATED: {
            if (action.payload.releaseId) {
                const currentRelease = state[action.payload.releaseId];
                if (currentRelease) {
                    return {
                        ...state,
                        [action.payload.releaseId]: {
                            ...currentRelease,
                            timelineEventsHistory: action.payload.events,
                        },
                    };
                }
            }
            return state;
        }
        case CLEAR_TIMELINE_EVENTS_HISTORY_ACTION: {
            if (action.payload.releaseId) {
                const currentRelease = state[action.payload.releaseId];
                if (currentRelease) {
                    return {
                        ...state,
                        [action.payload.releaseId]: {
                            ...currentRelease,
                            timelineEventsHistory: [],
                        },
                    };
                }
            }
            return state;
        }
        case FETCH_RELEASE_STATS_SUCCESS: {
            if (action.payload.releaseId) {
                const currentRelease = state[action.payload.releaseId];
                if (currentRelease) {
                    return {
                        ...state,
                        [action.payload.releaseId]: {
                            ...currentRelease,
                            releaseStatsHistory: action.payload.statistics,
                        },
                    };
                }
            }
            return state;
        }
        case FETCH_ROLLOUT_MONITORING_SUCCESS: {
            const currentRelease = state[action.payload.releaseId];
            if (currentRelease) {
                return {
                    ...state,
                    [action.payload.releaseId]: {
                        ...currentRelease,
                        rolloutMonitoring: action.payload.rolloutMonitoring,
                    },
                };
            }

            return state;
        }
        case FETCH_FEATURE_FLAGS_SUCCESS: {
            const currentRelease = state[action.payload.releaseId];
            if (currentRelease) {
                return {
                    ...state,
                    [action.payload.releaseId]: {
                        ...currentRelease,
                        featureFlags: action.payload.featureFlags,
                    },
                };
            }

            return state;
        }
        case ROLLOUT_MONITORING_FOR_RELEASES_UPDATED: {
            return updateRolloutMonitoringForReleases(state, action.payload);
        }
        case UPDATE_FEATURE_FLAGS: {
            return updateIndividualFeatureFlagForReleases(state, action.payload.releaseId, action.payload.featureFlags);
        }
        case FEATURE_FLAGS_FOR_RELEASES_UPDATED: {
            return updateFeatureFlagsForReleases(state, action.payload);
        }
        case FETCH_APP_STATE_SUCCESS:
            return createReleasesById(state, action.payload.app);
        case NEW_RELEASE_SUCCESS:
        case FETCH_RELEASE_SUCCESS:
        case UPDATE_RELEASE_SUCCESS:
        case UPDATE_RELEASE_ENTITY_SUCCESS:
            return updateReleaseEntityWithFullRelease(state, action.payload.release as Release);
        case RELEASES_UPDATED:
            return updateReleaseEntities(
                state,
                action.payload.map((connectedRelease) => connectedRelease.release),
            );
        case FETCH_RELEASES_SUCCESS:
            return updateReleasesById(state, action.payload.releases);
        case DELETE_RELEASE_ENTITY:
            return deleteRelease(state, action.payload.releaseId);
        case DELETE_RELEASES:
            return deleteReleases(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        case UPDATE_WORKFLOW_RUN_STEP_RUN_GRAPH: {
            // TODO: #WORKFLOWS# move this to workflowRun reducer whenever we migrate release to workflowRun
            let updatedState: ReleasesById = { ...state };

            for (const workflowRunStepRunGraph of action.payload) {
                if (workflowRunStepRunGraph.legacyReleaseId) {
                    const currentRelease = state[workflowRunStepRunGraph.legacyReleaseId];
                    if (currentRelease) {
                        updatedState = {
                            ...updatedState,
                            [workflowRunStepRunGraph.legacyReleaseId]: {
                                ...currentRelease,
                                stepRunGraph: workflowRunStepRunGraph,
                            },
                        };
                    }
                }
            }

            return updatedState;
        }
        default:
            return state;
    }
}

function allIds(state: string[] = [], action: ReleasesActionTypes) {
    switch (action.type) {
        case FETCH_APP_STATE_SUCCESS: {
            const allAppFetchedReleases = allReleases(action.payload.app).map((release) => release.id);
            return [...new Set([...state, ...allAppFetchedReleases])];
        }
        case UPDATE_RELEASE_SUCCESS: {
            return uniq([...state, action.payload.releaseId]);
        }
        case RELEASE_STATE_UPDATED:
        case NEW_RELEASE_SUCCESS:
        case UPDATE_RELEASE_ENTITY_SUCCESS: {
            return uniq([...state, action.payload.release.id]);
        }
        case FETCH_RELEASES_SUCCESS: {
            const allFetchedReleases = action.payload.releases.map((release) => release.id);
            return [...new Set([...state, ...allFetchedReleases])];
        }
        case RELEASES_UPDATED: {
            const releaseIds = action.payload.map((connectedRelease) => connectedRelease.release.id);
            const allReleaseIds = state.concat(releaseIds);
            return uniq(allReleaseIds);
        }
        case DELETE_RELEASE_ENTITY: {
            const releaseIds = state.filter((releaseId) => releaseId !== action.payload.releaseId);
            return releaseIds;
        }
        case DELETE_RELEASES: {
            const deletedReleaseIds = action.payload.map((data) => data.releaseId);
            return difference(state, deletedReleaseIds);
        }
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return [];
        default:
            return state;
    }
}

/*
    {
        byId: {
            1: {
                id: 1
                ...release
            },
            2: {
                id: 2
                ...release
            }
        },
        allIds: [1, 2]
    }

*/

const releasesReducer = combineReducers({
    byId: releasesById,
    allIds: allIds,
    // appHomeData:
    // sideBarData:
});

export default releasesReducer;
